import React, { useState } from 'react';
import { Button } from './ui/button';
import { usePlayerListContext } from '../lib/player-list.context';
import { Star, Check, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

interface AddToWishlistButtonProps {
  playerId: string;
  className?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export const AddToWishlistButton: React.FC<AddToWishlistButtonProps> = ({
  playerId,
  className,
  variant = 'outline',
  size = 'sm'
}) => {
  const { addPlayerToWishlist, isPlayerInWishlist } = usePlayerListContext();
  const [isAdding, setIsAdding] = useState(false);
  const [isAdded, setIsAdded] = useState(isPlayerInWishlist(playerId));
  const { toast } = useToast();

  const handleAddToWishlist = async () => {
    if (isAdded || isAdding) return;
    
    setIsAdding(true);
    
    // Check if there's an active KYP search
    const kypSearchElement = document.querySelector('[data-kyp-search-active="true"]');
    const searchId = kypSearchElement?.getAttribute('data-kyp-search-id');
    const searchStatus = kypSearchElement?.getAttribute('data-kyp-search-status');
    
    if (kypSearchElement) {
      console.log(`PERF:AddToWishlistButton: Adding player to wishlist during active KYP search. Search ID: ${searchId}, Status: ${searchStatus}`);
    }
    
    const startTime = performance.now();
    console.time('PERF:AddToWishlistButton:addPlayerToWishlist');
    
    try {
      const result = await addPlayerToWishlist(playerId);
      console.timeEnd('PERF:AddToWishlistButton:addPlayerToWishlist');
      const endTime = performance.now();
      const duration = endTime - startTime;
      console.log(`PERF:AddToWishlistButton: Adding player to wishlist took ${duration.toFixed(2)}ms`);
      
      if (result) {
        setIsAdded(true);
        toast({
          title: "Added to Shortlist",
          description: "Player has been added to your shortlist",
          variant: "default",
        });
        
        // Store the timestamp when the player was added to the wishlist
        sessionStorage.setItem('lastWishlistAddTime', endTime.toString());
        sessionStorage.setItem('lastPage', 'kyp-search');
        
        // Log the result for debugging
        console.log(`PERF:AddToWishlistButton: Wishlist updated successfully with ${result.items.length} items`);
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      toast({
        title: "Error",
        description: "Failed to add player to wishlist",
        variant: "destructive",
      });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Button
      onClick={handleAddToWishlist}
      variant={variant}
      size={size}
      className={`bg-transparent hover:bg-[#4E46FF] text-[#CECFD2] hover:text-white transition-colors ${className || ''}`}
      disabled={isAdded || isAdding}
    >
      {isAdding ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Adding...
        </>
      ) : isAdded ? (
        <>
          <Check className="h-4 w-4 mr-2" />
          Added to Shortlist
        </>
      ) : (
        <>
          <Star className="h-4 w-4 mr-2" />
          Add Player to Shortlist
        </>
      )}
    </Button>
  );
};